import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Box, Paper, Typography, Divider } from '@mui/material'
import LedgerProvider, { useLedger } from './LedgerContext'
import { loadBalances, loadPayouts, FUND_ACCOUNTS, loadConsistency } from './api'
import ConsistencyCheck from './ConsistencyCheck'
import FundsSummary from './FundsSummary'
import RecentTransactionsPanel from './RecentTransactionsPanel'
import WalletsPanel from './WalletsPanel'
import PayoutsPanel from './PayoutsPanel'
import axios from 'axios'
import { API_BASE, authHeaders } from './api'

function Inner() {
  const [balancesRaw, setBalancesRaw] = useState([])
  const [consistency, setConsistency] = useState([])
  const [payoutRows, setPayoutRows] = useState([])
  const [loadingPayouts, setLoadingPayouts] = useState(false)
  const [activePanel, setActivePanel] = useState('recent')
  const [selectedWalletUserId, setSelectedWalletUserId] = useState(null)
  const { filters, setFilters } = useLedger()

  const [totalFiltered, setTotalFiltered] = useState({ sumAbs: 0, count: 0, loading: false })

  useEffect(() => { loadBalances().then(setBalancesRaw) }, [])
  useEffect(() => { reloadPayouts() }, [])
  useEffect(() => { loadConsistency().then(setConsistency) }, [])

  const reloadPayouts = async () => {
    setLoadingPayouts(true)
    setPayoutRows(await loadPayouts())
    setLoadingPayouts(false)
  }

  const funds = useMemo(() => {
    const agg = { CASHBACK_FUND: 0, REFERRAL_FUND: 0, MARGIN_FUND: 0, VAT_FUND: 0 }
    for (const r of balancesRaw) {
      const kind = r.account || r.bucket
      if (FUND_ACCOUNTS.includes(kind)) {
        agg[kind] = (agg[kind] || 0) + Number(r.balance || 0)
      }
    }
    return agg
  }, [balancesRaw])

  const wallets = useMemo(
    () => balancesRaw
      .filter(r => (r.account || r.bucket) === 'USER_WALLET')
      .map(r => ({ userId: r.userId, balance: Number(r.balance || 0), currency: r.currency || 'EUR' })),
    [balancesRaw]
  )
  const walletsTotal = useMemo(() => wallets.reduce((s, w) => s + Number(w.balance || 0), 0), [wallets])
  const payoutTotal = useMemo(() => payoutRows.reduce((s, r) => s + Number(r.amount || 0), 0), [payoutRows])

    const computeFilteredTotals = useCallback(async () => {
    setTotalFiltered(s => ({ ...s, loading: true }))
    let sumAbs = 0
    try {
      const limit = 500
      let offset = 0
      for (let page = 0; page < 400; page++) {
        const p = new URLSearchParams()
        Object.entries(filters).forEach(([k, v]) => {
          if (v === '' || v === null || v === undefined) return
          if (k === 'limit') return
          p.set(k, String(v))
        })
        p.set('limit', String(limit))
        p.set('offset', String(offset))
        const res = await axios.get(`${API_BASE}/api/ledger?${p.toString()}`, { headers: authHeaders() })
        const arr = Array.isArray(res.data) ? res.data : []
        for (const r of arr) sumAbs += Math.abs(Number(r.amount || 0))
        if (arr.length < limit) break
        offset += limit
      }
      setTotalFiltered({ sumAbs, count: 0, loading: false })
    } catch {
      setTotalFiltered({ sumAbs: 0, count: 0, loading: false })
    }
  }, [filters])

  useEffect(() => {
    if (activePanel === 'recent') computeFilteredTotals()
  }, [activePanel, computeFilteredTotals])

  const onFundClick = (acc) => {
    setFilters(f => ({ ...f, account: acc }))
    setActivePanel('recent'); setSelectedWalletUserId(null)
  }
  const onShowAllTransactions = () => {
    setActivePanel('recent')
    setSelectedWalletUserId(null)
    setFilters({ userId: '', account: '', source: '', dateFrom: '', dateTo: '', limit: 50 })
  }
  const onToggleWallets = () => { setActivePanel('wallets'); setSelectedWalletUserId(null) }
  const onTogglePayouts = () => { setActivePanel('payouts'); reloadPayouts() }

return (
  <Box
    sx={{
      background: '#ededed',
      padding: 1.5,
      border: '1.5px solid #107c6b',
      borderRadius: '10px',
      width: '100%',
      boxSizing: 'border-box',
      position: 'relative',
      height: '100%',
      minHeight: 0,
      display: 'flex',
      flexDirection: 'column',
      gap: 2,
      overflow: 'auto'
    }}
  >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Typography variant="h5" sx={{ flex: 1 }}>Účtovná kniha</Typography>
      </Box>

      <FundsSummary
        funds={funds}
        recent50SumAbs={totalFiltered.sumAbs}
        walletsTotal={walletsTotal}
        payoutTotal={payoutTotal}
        onFundClick={onFundClick}
        onShowAllTransactions={onShowAllTransactions}
        onToggleWallets={onToggleWallets}
        onTogglePayouts={onTogglePayouts}
      />

      <Paper sx={{ p: 2 }}>
        <ConsistencyCheck rows={consistency} />
      </Paper>

      <Divider />

      {activePanel === 'recent' && (
        <RecentTransactionsPanel filters={filters} setFilters={setFilters} />
      )}
      {activePanel === 'wallets' && (
        <WalletsPanel
          wallets={wallets}
          selectedWalletUserId={selectedWalletUserId}
          setSelectedWalletUserId={setSelectedWalletUserId}
          filters={filters}
        />
      )}
      {activePanel === 'payouts' && (
        <PayoutsPanel payoutRows={payoutRows} loadingPayouts={loadingPayouts} reload={reloadPayouts} limit={filters.limit} />
      )}
    </Box>
  )
}

export default function AdminLedger() {
  return (
    <LedgerProvider>
      <Inner />
    </LedgerProvider>
  )
}
